import * as THREE from 'three';
import { isPinching, type Point } from '../engine/pinch.js';

/**
 * The student's own hands, drawn from tracked joints.
 *
 * Quest reports 25 joints per hand with a radius each, and this draws a
 * sphere at every one. Deliberately not three.js's `XRHandModelFactory`: its
 * models are GLTF files fetched from outside the app, and our CSP is
 * `default-src 'self'` (index.html) because a headset in a school should not
 * be reaching out to anybody's CDN mid-lesson. Twenty-five spheres need no
 * assets, no licence and no network.
 *
 * Joints rather than a mesh is also the honest thing to draw: it shows the
 * student exactly what the headset can see of their hand, so a finger that
 * has lost tracking looks lost rather than looking like a hand that is
 * ignoring them.
 *
 * Controllers still work and are not replaced. A school will have both, and
 * a child whose hands will not track -- sleeves, dark room, a plaster cast --
 * has to be able to do the lesson anyway (ADR-025).
 */

/** One hand, as the lesson sees it. */
export type HandState = {
  /** Where a pinch is happening, between thumb and index fingertip. */
  pinchPoint: THREE.Vector3;
  pinching: boolean;
  /** True on the frame a pinch began, and on the frame it ended. */
  justPinched: boolean;
  justReleased: boolean;
  /** False while the headset cannot see this hand at all. */
  tracked: boolean;
};

const JOINT_COLOUR = 0x9ec5ff;

/**
 * Bright enough to find against a dark scene, transparent enough not to hide
 * what the hand is holding -- a child assembling a skeleton is looking at the
 * bone, not at their knuckles.
 */
function jointMaterial(): THREE.Material {
  return new THREE.MeshBasicMaterial({ color: JOINT_COLOUR, transparent: true, opacity: 0.75 });
}

class Hand {
  readonly group = new THREE.Group();
  readonly state: HandState = {
    pinchPoint: new THREE.Vector3(),
    pinching: false,
    justPinched: false,
    justReleased: false,
    tracked: false,
  };

  readonly #spheres = new Map<string, THREE.Mesh>();
  readonly #geometry = new THREE.SphereGeometry(1, 8, 6);
  readonly #material = jointMaterial();

  constructor(readonly space: THREE.XRHandSpace) {
    this.group.matrixAutoUpdate = false;
    space.add(this.group);
  }

  /**
   * Reads this frame's joints.
   *
   * three.js fills `joints` from the XRFrame, so there is nothing to ask for
   * here; a hand that is not being tracked simply has none, which is what
   * `tracked` reports.
   */
  update(): void {
    const joints = this.space.joints as Record<string, THREE.XRJointSpace | undefined>;
    const thumb = joints['thumb-tip'];
    const index = joints['index-finger-tip'];

    const tracked = Boolean(thumb?.visible && index?.visible);
    this.state.tracked = tracked;
    this.group.visible = tracked;

    if (!tracked || !thumb || !index) {
      // A hand that vanishes mid-pinch is a hand that let go. Carrying the
      // pinch across a tracking loss would leave a bone stuck to a hand that
      // is not on screen.
      this.state.justReleased = this.state.pinching;
      this.state.justPinched = false;
      this.state.pinching = false;
      return;
    }

    this.#drawJoints(joints);

    const thumbAt = thumb.position;
    const indexAt = index.position;
    const gap: [Point, Point] = [
      [thumbAt.x, thumbAt.y, thumbAt.z],
      [indexAt.x, indexAt.y, indexAt.z],
    ];

    const wasPinching = this.state.pinching;
    const pinching = isPinching(gap[0], gap[1], wasPinching);
    this.state.justPinched = pinching && !wasPinching;
    this.state.justReleased = !pinching && wasPinching;
    this.state.pinching = pinching;

    // Between the fingertips rather than at either: it is where a child sees
    // themselves holding something, and it is what the bone is snapped to.
    this.state.pinchPoint.copy(thumbAt).add(indexAt).multiplyScalar(0.5);
    thumb.parent?.localToWorld(this.state.pinchPoint);
  }

  #drawJoints(joints: Record<string, THREE.XRJointSpace | undefined>): void {
    for (const [name, joint] of Object.entries(joints)) {
      if (!joint) continue;

      let sphere = this.#spheres.get(name);
      if (!sphere) {
        sphere = new THREE.Mesh(this.#geometry, this.#material);
        this.#spheres.set(name, sphere);
        this.group.add(sphere);
      }

      sphere.visible = joint.visible;
      if (!joint.visible) continue;
      sphere.position.copy(joint.position);
      sphere.quaternion.copy(joint.quaternion);
      // The radius the headset reports, so a child's hand looks like a
      // child's hand rather than an adult's.
      const radius = joint.jointRadius ?? 0.008;
      sphere.scale.setScalar(radius);
    }
    this.group.updateMatrix();
  }

  dispose(): void {
    this.#geometry.dispose();
    this.#material.dispose();
  }
}

/** Both hands, if the headset is tracking them. */
export class Hands {
  readonly #hands: Hand[] = [];

  constructor(renderer: THREE.WebGLRenderer, rig: THREE.Object3D) {
    for (let i = 0; i < 2; i++) {
      const space = renderer.xr.getHand(i);
      rig.add(space);
      this.#hands.push(new Hand(space));
    }
  }

  /** Called once a frame, before anything reads a hand. */
  update(): void {
    for (const hand of this.#hands) hand.update();
  }

  get states(): readonly HandState[] {
    return this.#hands.map((hand) => hand.state);
  }

  /** The hands actually being tracked right now. Empty with controllers. */
  get tracked(): readonly HandState[] {
    return this.states.filter((state) => state.tracked);
  }

  dispose(): void {
    for (const hand of this.#hands) hand.dispose();
  }
}
